import { lazy, Suspense, useState } from 'react';
import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';
import { ScanLine, AlertTriangle, UserSearch } from 'lucide-react';

// IDScanner pulls in zxing-wasm + @zxing/browser + @zxing/library —
// a multi-MB dependency only used when the operator taps "Scan ID".
// Lazy-load so the camera + decoder stack isn't in the main ePCR
// bundle (Sprint-4 Finding 71). The Suspense fallback around the
// modal mount shows nothing — the scanner UI itself is the loading
// state and the chunk fetch is sub-second on warm caches.
const IDScanner = lazy(() => import('@/components/id-scanner/IDScanner').then(m => ({ default: m.IDScanner })));
import { IDScanConfirmation } from '@/components/id-scanner/IDScanConfirmation';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoPatientFields } from '@/lib/demo-data';
import { TouchDateInput } from '@/components/TouchDateInput';
import { FanOutChips } from '@/components/FanOutChips';
import { PatientSearchModal, DangerFlagBanner } from '@/components/PatientSearchModal';
import type { PatientDangerFlag } from '@/lib/patient-registry';
import type { AAMVAData } from '@/lib/aamva-parser';

// Patterns caught by DB constraint — we warn early so user isn't surprised at QA
const FAKE_NAME_RE = /^(jane doe|john doe|doe, jane|doe, john|unknown|n\/?a|none|test|xxx|\?+)$/i;
const FAKE_DOB_VALUES = new Set(['1900-01-01', '1901-01-01', '0001-01-01']);

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

const GENDER_OPTIONS = ['Male', 'Female', 'Non-Binary', 'Unknown'];
const RACE_OPTIONS = [
  'American Indian/Alaska Native', 'Asian', 'Black/African American',
  'Hispanic/Latino', 'Native Hawaiian/Pacific Islander', 'White', 'Other', 'Unknown',
];

export function PatientDataTab({ epcr, onUpdate }: Props) {
  // Scanner flow:
  //   'idle'        → button visible
  //   'scanning'    → IDScanner camera modal open
  //   'confirming'  → IDScanConfirmation review modal open with parsed data
  const [scanState, setScanState] = useState<'idle' | 'scanning' | 'confirming'>('idle');
  const [scanResult, setScanResult] = useState<AAMVAData | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dangerFlags, setDangerFlags] = useState<PatientDangerFlag[]>([]);

  // Field isolation: this handler ONLY writes to patient demographic fields.
  // It NEVER touches incident.address, dispatch info, billing, hospital destination,
  // narrative, or any other section. Per ID Scan Spec §10.
  const handleConfirmedScan = (updates: Partial<EPCR>) => {
    onUpdate(updates);
    setScanState('idle');
    setScanResult(null);
  };

  // History, medications, allergies use PredictiveChipList (see below).
  // No arbitrary limit; every entry is free-text compatible.

  return (
    <div className="space-y-4">
      {/* Search modal */}
      {searchOpen && (
        <PatientSearchModal
          epcr={epcr}
          onApply={(patch, flags) => {
            onUpdate(patch);
            setDangerFlags(flags);
          }}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {/* Danger flags from a prior encounter, if recall surfaced any. */}
      <DangerFlagBanner flags={dangerFlags} />

      {/* Top row: Recall + Scan ID side-by-side at the same size, plus
          the demo fill on the right. The two patient-source affordances
          (look up an existing record vs scan the license barcode) sit
          together at the top so the EMT can pick whichever is fastest
          for the patient in front of them. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {!epcr.locked && (
            <button
              onClick={() => setSearchOpen(true)}
              className="rail-button rail-action"
            >
              <UserSearch size={14} />
              Recall existing patient
            </button>
          )}
          {!epcr.locked && (
            <button
              onClick={() => setScanState('scanning')}
              className="rail-button rail-action"
            >
              <ScanLine size={14} />
              Scan ID
            </button>
          )}
        </div>
        <DemoFillButton onFill={() => onUpdate(demoPatientFields())} />
      </div>

      {/* ID Scanner — camera + decode loop. Lazy-loaded; Suspense
          fallback is null because the scanner mounts a full-screen
          modal — a brief blank moment is better than a flash of
          loading chrome before the camera viewfinder. */}
      {scanState === 'scanning' && (
        <Suspense fallback={null}>
          <IDScanner
            onScanComplete={(data) => {
              setScanResult(data);
              setScanState('confirming');
            }}
            onCancel={() => setScanState('idle')}
            onManualEntry={() => setScanState('idle')}
          />
        </Suspense>
      )}

      {/* Confirmation modal — review parsed data before write */}
      {scanState === 'confirming' && scanResult && (
        <IDScanConfirmation
          scan={scanResult}
          existingPatient={epcr}
          onConfirm={handleConfirmedScan}
          onRescan={() => {
            setScanResult(null);
            setScanState('scanning');
          }}
          onCancel={() => {
            setScanResult(null);
            setScanState('idle');
          }}
        />
      )}

      {/* Demographics */}
      <section>
        {/* Scan ID lives in the top-row toolbar alongside Recall. The
            Demographics header used to host its own duplicate Scan
            button — removed in favor of the single canonical control. */}
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-slate-200">Demographics</h3>
        </div>

        {/* Scan provenance indicator */}
        {epcr.patient_id_scan_source === 'barcode_scan' && epcr.patient_id_scan_timestamp && (
          <div className="mb-3 flex items-center gap-2 text-xs text-brand-300 bg-brand-500/10 border border-brand-500/30 rounded-lg px-3 py-1.5">
            <ScanLine size={14} />
            <span>
              Demographics populated from barcode scan on{' '}
              {new Date(epcr.patient_id_scan_timestamp).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
              {epcr.patient_id_scan_status && ` (${epcr.patient_id_scan_status})`}
            </span>
          </div>
        )}

        {/* Tightened grid gap — gap-y-2 (8 px) instead of the previous
            gap-4 (16 px) so name fields and gender sit closer; the
            FanOutChips strip below Last Name otherwise inflated the
            visual gap before the Gender row. Horizontal gap stays at
            16 px so adjacent column inputs don't crowd each other. */}
        <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
          {/* Social Security Number — top of the grid, full width.
              Stored encrypted. Required by some payers; safe to leave
              blank if the patient declines. */}
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-300">
              Social Security Number
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9-]*"
              value={epcr.patient_ssn_encrypted ?? ''}
              onChange={(e) => onUpdate({ patient_ssn_encrypted: e.target.value || null })}
              className="input-field font-mono"
              placeholder="###-##-####"
              autoComplete="off"
              disabled={epcr.locked}
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Stored encrypted. Required by some payers for claim adjudication; leave blank if patient declines.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">First Name</label>
            <input
              type="text"
              value={epcr.patient_first_name ?? ''}
              onChange={(e) => onUpdate({ patient_first_name: e.target.value || null })}
              className="input-field"
              placeholder="First name"
              disabled={epcr.locked}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">Middle Name</label>
            <input
              type="text"
              value={epcr.patient_middle_name ?? ''}
              onChange={(e) => onUpdate({ patient_middle_name: e.target.value || null })}
              className="input-field"
              placeholder="Middle name (optional)"
              disabled={epcr.locked}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">Last Name</label>
            <input
              type="text"
              value={epcr.patient_last_name ?? ''}
              onChange={(e) => onUpdate({ patient_last_name: e.target.value || null })}
              className={cn(
                'input-field',
                epcr.patient_last_name && FAKE_NAME_RE.test(epcr.patient_last_name) && 'border-ems-amber',
              )}
              placeholder="Last name"
              disabled={epcr.locked}
            />
            {epcr.patient_last_name && FAKE_NAME_RE.test(epcr.patient_last_name) && (
              <p className="mt-1 flex items-center gap-1 text-xs text-ems-amber">
                <AlertTriangle size={12} />
                Placeholder values (Jane/John Doe, Unknown, etc.) are blocked at finalization. Leave blank if truly unknown.
              </p>
            )}
            <FanOutChips capture="patient.last_name" />
          </div>
          <div>
            <TouchDateInput
              label="Date of Birth"
              value={epcr.patient_dob}
              onChange={(v) => onUpdate({ patient_dob: v })}
              disabled={epcr.locked}
              warn={!!epcr.patient_dob && FAKE_DOB_VALUES.has(epcr.patient_dob)}
              hint="MM / DD / YYYY · tap age chips to estimate when unknown"
            />
            {epcr.patient_dob && FAKE_DOB_VALUES.has(epcr.patient_dob) && (
              <p className="mt-1 flex items-center gap-1 text-xs text-ems-amber">
                <AlertTriangle size={12} />
                Placeholder DOBs (1900-01-01, etc.) are blocked. Leave blank if unknown.
              </p>
            )}
            <FanOutChips capture="patient.dob" />
          </div>
          {/* Race + Gender stacked across the full grid width below
              the names. Race is the dropdown (one row, full grid
              width); Gender is the 4-button row directly below it.
              Suffix removed — long-tail field that wasn't earning
              its grid slot. */}
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-300">Race</label>
            <select
              value={epcr.patient_race ?? ''}
              onChange={(e) => onUpdate({ patient_race: e.target.value || null })}
              className="input-field"
              disabled={epcr.locked}
            >
              <option value="">Select...</option>
              {RACE_OPTIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-300">Gender</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {GENDER_OPTIONS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => onUpdate({ patient_gender: g })}
                  disabled={epcr.locked}
                  className={cn(
                    'rounded-lg px-3 py-2.5 text-sm font-medium min-h-touch transition-colors',
                    epcr.patient_gender === g
                      ? 'bg-brand-500 text-white'
                      : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                  )}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Patient address (separated fields) */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-slate-300">Street Address</label>
            <input
              type="text"
              value={epcr.patient_address ?? ''}
              onChange={(e) => onUpdate({ patient_address: e.target.value || null })}
              className="input-field"
              placeholder="Street address (patient home, not incident)"
              disabled={epcr.locked}
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Patient home address. Incident location is set on the Incident page.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">City</label>
            <input
              type="text"
              value={epcr.patient_city ?? ''}
              onChange={(e) => onUpdate({ patient_city: e.target.value || null })}
              className="input-field"
              disabled={epcr.locked}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">State</label>
              <input
                type="text"
                value={epcr.patient_state ?? ''}
                onChange={(e) => onUpdate({ patient_state: e.target.value.toUpperCase() || null })}
                className="input-field"
                placeholder="RI"
                maxLength={2}
                disabled={epcr.locked}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">ZIP</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9-]*"
                value={epcr.patient_zip ?? ''}
                onChange={(e) => onUpdate({ patient_zip: e.target.value || null })}
                className="input-field"
                placeholder="02903"
                maxLength={10}
                disabled={epcr.locked}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Government ID identification */}
      <section>
        <h3 className="mb-3 text-lg font-semibold text-slate-200">Government ID</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">ID Number</label>
            <input
              type="text"
              value={epcr.patient_id_number ?? ''}
              onChange={(e) => onUpdate({ patient_id_number: e.target.value || null })}
              className="input-field"
              placeholder="Driver's license / state ID number"
              disabled={epcr.locked}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">ID Type</label>
            <select
              value={epcr.patient_id_type ?? ''}
              onChange={(e) => onUpdate({ patient_id_type: (e.target.value || null) as EPCR['patient_id_type'] })}
              className="input-field"
              disabled={epcr.locked}
            >
              <option value="">Select...</option>
              <option value="driver_license">Driver&apos;s License</option>
              <option value="state_id">State ID</option>
              <option value="military">Military ID</option>
              <option value="passport">Passport</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-300">Issuing Jurisdiction</label>
            <input
              type="text"
              value={epcr.patient_id_state ?? ''}
              onChange={(e) => onUpdate({ patient_id_state: e.target.value.toUpperCase() || null })}
              className="input-field"
              placeholder="2-letter state/province (RI)"
              maxLength={2}
              disabled={epcr.locked}
            />
          </div>
          {/* ID issue / expiration dates removed per Cameron — clutter
              for an EMS workflow. The AAMVA parser still captures
              them when an ID is scanned (they go into the underlying
              record for audit), but they don't get a UI input. */}
        </div>
      </section>

      {/* Billing Identity section removed — Social Security Number
          now lives at the top of the Demographics grid (full-width
          row) so the EMT enters identity once at the top instead of
          jumping back to a separate section after demographics.
          Insurance carrier / policy / relationship still live on
          the Billing tab (§ 4 module). */}

      {/* Medical history, medications, and allergies live on their own
          tab ("History") — per addendum, history must be independent of
          patient demographics so the Command-view layout doesn't stack
          ID + clinical data in a single frame. */}
    </div>
  );
}

